import { useEffect, useState } from 'react';
import { PriceBreakdown } from '../../api/booking/pricePreviewApiTypes';

export interface UseReservationSummaryParams {
    /**
     * Persisted booking total — used only by the view pages, which don't receive
     * a `breakdown` yet (booking-details responses don't embed one; #1203 leftover,
     * BE). Create/edit surfaces omit it and pass `breakdown` instead.
     */
    total?: number;
    couponApplied?: number;
    amountPaid?: number;
    /**
     * Lower bound for the discount input. `0` = discount only (storage/SUP);
     * a negative value (e.g. -1000) additionally allows surcharges (venue/room).
     */
    minDiscount?: number;
    onDiscountChange?: (value: number) => void;
    onAlreadyPaidChange?: (value: number) => void;
    /**
     * Backend price breakdown (#1203). When provided, the displayed numbers
     * (net, tax, adjusted total, amount due, committed discount) come straight
     * from the server — no FE re-derivation. The inline-edit machinery below is
     * unchanged; it just drives the inputs that re-trigger the preview call.
     */
    breakdown?: PriceBreakdown;
}

/**
 * Single source of truth for the reservation price box: discount inline-edit state,
 * already-paid inline-edit state, and the tax / net / amount-due math. All three
 * price-summary components (venue, room, non-room rental) derive their numbers from
 * here so the calculations live in one place (#1191, hardening from #999).
 *
 * Validation and derived totals are based on form state (`total`, the temp inputs),
 * never on the raw API response.
 */
export function useReservationSummary({
    total = 0,
    couponApplied,
    amountPaid,
    minDiscount = 0,
    onDiscountChange,
    onAlreadyPaidChange,
    breakdown,
}: UseReservationSummaryParams) {
    const committedDiscount = breakdown ? breakdown.discount : (couponApplied ?? 0);
    // Discount is measured/clamped against the pre-discount total. With a BE
    // breakdown that is `subtotal`; otherwise the form-state `total` prop.
    const discountBound = breakdown ? breakdown.subtotal : total;

    const [editingDiscount, setEditingDiscount] = useState(false);
    const [tempDiscount, setTempDiscount] = useState(committedDiscount.toString());

    const [editingAlreadyPaid, setEditingAlreadyPaid] = useState(false);
    const [tempAlreadyPaid, setTempAlreadyPaid] = useState(amountPaid ?? 0);

    // Keep the temp inputs in sync when the committed values change from the parent.
    useEffect(() => {
        if (couponApplied !== undefined) {
            setTempDiscount(couponApplied.toString());
        }
    }, [couponApplied]);

    useEffect(() => {
        setTempAlreadyPaid(amountPaid ?? 0);
    }, [amountPaid]);

    const rawTempDiscount = Number.parseFloat(tempDiscount);
    const numericTempDiscount = Number.isNaN(rawTempDiscount) ? 0 : rawTempDiscount;
    const isDiscountValid = numericTempDiscount <= discountBound && numericTempDiscount >= minDiscount;

    const clampDiscount = (value: number) => Math.min(Math.max(value, minDiscount), discountBound);

    const confirmDiscount = () => {
        const clamped = clampDiscount(numericTempDiscount);
        if (clamped !== numericTempDiscount) {
            setTempDiscount(clamped.toString());
        }
        onDiscountChange?.(clamped);
        setEditingDiscount(false);
    };

    const cancelDiscount = () => {
        setTempDiscount(committedDiscount.toString());
        setEditingDiscount(false);
    };

    const confirmAlreadyPaid = () => {
        onAlreadyPaidChange?.(tempAlreadyPaid);
        setEditingAlreadyPaid(false);
    };

    const cancelAlreadyPaid = () => {
        setTempAlreadyPaid(amountPaid ?? 0);
        setEditingAlreadyPaid(false);
    };

    // Derived money numbers — all of them the backend's (#1203, #1353).
    //
    // The FE net/tax split that used to live here is gone. It divided the whole
    // total by `1 + taxRate`, so it could only ever express ONE rate for a
    // booking, which taxed a 7% room's cleaning add-on at 7%. It also did not
    // round, so it and the backend could disagree by sub-cent on the same
    // booking. VAT is now resolved per line and rounded once per rate group
    // server-side; this hook reports what it is told.
    const computedAdjustedTotal = total - committedDiscount;

    const adjustedTotal = breakdown ? breakdown.total : computedAdjustedTotal;
    // Without a breakdown there is no tax answer, and `0` would be one — a
    // tenant who charges no VAT and a booking whose VAT is unknown must not
    // render identically. The components below treat null as "no tax block".
    const net = breakdown ? breakdown.net : null;
    const tax = breakdown ? breakdown.tax : null;
    const amountDue = breakdown
        ? breakdown.amountDue
        : total - (amountPaid ?? 0) - committedDiscount;

    return {
        // discount edit
        editingDiscount,
        setEditingDiscount,
        tempDiscount,
        setTempDiscount,
        numericTempDiscount,
        isDiscountValid,
        confirmDiscount,
        cancelDiscount,
        // already-paid edit
        editingAlreadyPaid,
        setEditingAlreadyPaid,
        tempAlreadyPaid,
        setTempAlreadyPaid,
        confirmAlreadyPaid,
        cancelAlreadyPaid,
        // derived numbers
        committedDiscount,
        adjustedTotal,
        net,
        tax,
        amountDue,
        // BE breakdown (#1203) — null on legacy-computed surfaces
        breakdown: breakdown ?? null,
        paymentStatus: breakdown?.paymentStatus ?? null,
    };
}

export type ReservationSummaryState = ReturnType<typeof useReservationSummary>;
